import React, { useEffect, useMemo, useState } from 'react';
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    PaperClipIcon,
    PlusCircleIcon,
    ScaleIcon,
} from '@heroicons/react/24/outline';
import {
    createMaterialLot,
    fetchCompostMaterials,
    fetchExpensePayments,
    fetchExpenseReviewers,
    fetchMaterialLot,
    fetchMaterialLots,
    fetchMaterialSummary,
    fetchMaterialVendors,
    createMaterialVendor,
    recordMaterialPurchase,
    recordExpensePayment,
    assignExpenseReviewer,
    recordMaterialReceipt,
    type ActivityMaterialOption,
    type MaterialLot,
    type MaterialVendor,
    type MaterialStockSummaryRow,
} from '../../utils/activityApi';
import { useAuth } from '../../contexts/AuthContext';

const STAFF_ROLES = ['super_admin', 'project_manager', 'tenant_admin', 'business_manager', 'field_manager', 'agronomist', 'agronomist_advisor'];

const PRODUCT_LABELS: Record<string, string> = {
    compost: 'Compost',
    manure: 'Manure',
    mineral_fertilizer: 'Mineral fertiliser',
    other: 'Other',
};

const PAYMENT_STYLES: Record<string, string> = {
    paid: 'bg-emerald-100 text-emerald-800',
    partial: 'bg-amber-100 text-amber-800',
    reversed: 'bg-red-100 text-red-700',
    unpaid: 'bg-slate-100 text-slate-600',
};

const num = (value: any): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const daysUntil = (date?: string | null): number | null => {
    if (!date) return null;
    const target = new Date(`${date}T00:00:00`);
    if (Number.isNaN(target.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - today.getTime()) / 86400000);
};

const asArray = (value: any): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const PURCHASE_PRODUCTS = [
    ['compost', 'Compost'],
    ['manure', 'Manure'],
    ['mineral_fertilizer', 'Mineral fertiliser'],
    ['other', 'Other'],
] as const;

const newIdempotencyKey = (): string => {
    const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `stock-${random}`;
};

const FieldLabel: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
    <label className="block">
        <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">{label}</span>
        <div className="mt-1">{children}</div>
        {hint && <span className="mt-1 block text-[11px] font-semibold text-slate-400">{hint}</span>}
    </label>
);

const inputClass = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20';

/**
 * Inputs / stock workspace: what compost, manure and fertiliser the farm
 * actually holds, where it came from, whether it was paid for, and what has
 * gone to the field. Cash is never shown as a total here: the one expense per
 * purchase lives in Owner Money, and a stock movement is not a payment.
 */
const MaterialStockWorkspace: React.FC = () => {
    const { user } = useAuth();
    const canWrite = STAFF_ROLES.includes(String(user?.role || ''));
    const canSettle = ['owner', 'project_manager', 'tenant_admin', 'business_manager', 'finance_admin'].includes(String(user?.role || ''));

    const [lots, setLots] = useState<MaterialLot[]>([]);
    const [summary, setSummary] = useState<MaterialStockSummaryRow[]>([]);
    const [vendors, setVendors] = useState<MaterialVendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [productFilter, setProductFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [paymentFilter, setPaymentFilter] = useState('all');
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [movements, setMovements] = useState<Record<number, any[]>>({});
    const [payments, setPayments] = useState<Record<number, any[]>>({});
    const [reviewers, setReviewers] = useState<Array<{ id: number; full_name: string; role: string }>>([]);
    const [receiptDraft, setReceiptDraft] = useState('');
    const [receiptNote, setReceiptNote] = useState('');
    const [busyId, setBusyId] = useState<number | null>(null);
    const [actionMessage, setActionMessage] = useState('');
    const [actionError, setActionError] = useState('');

    // The chain starts here: one purchase = one expense + one lot. Opening the
    // form mints the idempotency key so a double-click cannot buy twice.
    const [entryOpen, setEntryOpen] = useState(false);
    const [entryMode, setEntryMode] = useState<'purchased' | 'farm_produced'>('purchased');
    const [entryKey, setEntryKey] = useState('');
    const [entryBusy, setEntryBusy] = useState(false);
    const [entryError, setEntryError] = useState('');
    const [vendorDraft, setVendorDraft] = useState('');
    const [vendorCreateOpen, setVendorCreateOpen] = useState(false);
    const [vendorBusy, setVendorBusy] = useState(false);
    const [paymentDraft, setPaymentDraft] = useState({ amount: '', method: 'mobile_money', reference: '', proof_url: '' });
    const [paymentBusy, setPaymentBusy] = useState(false);
    const [reviewerBusy, setReviewerBusy] = useState<number | null>(null);
    const [paymentKeys, setPaymentKeys] = useState<Record<number, string>>({});
    const [batches, setBatches] = useState<ActivityMaterialOption[]>([]);
    const [batchesLoading, setBatchesLoading] = useState(false);
    const [entry, setEntry] = useState({
        product_type: 'compost',
        product_name: '',
        unit: 'kg',
        quantity: '',
        vendor_id: '',
        supplier_name: '',
        amount: '',
        currency: 'RWF',
        spent_on: '',
        received_on: '',
        expiry_date: '',
        quality_notes: '',
        notes: '',
        compost_batch_id: '',
    });

    const setEntryValue = (key: keyof typeof entry, value: string) => {
        setEntry((prev) => ({ ...prev, [key]: value }));
    };

    const submitVendor = async () => {
        const displayName = vendorDraft.trim();
        if (!displayName) return;
        setVendorBusy(true);
        setEntryError('');
        try {
            const vendor = await createMaterialVendor({
                display_name: displayName,
                product_categories: [entry.product_type],
            });
            setVendors((current) => [...current.filter((item) => item.id !== vendor.id), vendor].sort((a, b) => a.display_name.localeCompare(b.display_name)));
            setEntry((current) => ({ ...current, vendor_id: String(vendor.id), supplier_name: vendor.display_name }));
            setVendorDraft('');
            setVendorCreateOpen(false);
        } catch (err: any) {
            setEntryError(err?.message || 'Supplier could not be added.');
        } finally {
            setVendorBusy(false);
        }
    };

    const openEntry = async (mode: 'purchased' | 'farm_produced') => {
        setEntryMode(mode);
        setEntryOpen(true);
        setEntryError('');
        setActionMessage('');
        setEntryKey(newIdempotencyKey());
        if (mode === 'farm_produced' && batches.length === 0 && !batchesLoading) {
            setBatchesLoading(true);
            try {
                const options = await fetchCompostMaterials();
                setBatches(options.filter((option) => option.source === 'compost_batch'));
            } catch {
                // The picker stays empty and the form says a batch is required.
            } finally {
                setBatchesLoading(false);
            }
        }
    };

    const submitEntry = async () => {
        setEntryError('');
        const productName = entry.product_name.trim();
        const quantity = Number(entry.quantity);
        if (!productName) {
            setEntryError('Say what the material is.');
            return;
        }
        if (!Number.isFinite(quantity) || quantity <= 0) {
            setEntryError('Enter the quantity that physically arrived.');
            return;
        }
        if (entryMode === 'farm_produced' && !entry.compost_batch_id) {
            setEntryError('Farm-produced compost must be tied to the compost batch it came from.');
            return;
        }
        if (entryMode === 'purchased') {
            const amount = Number(entry.amount);
            if (!entry.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
                setEntryError(
                    'Enter the price of this purchase. That becomes its one expense; goods that arrived with no price are recorded against their delivery instead.',
                );
                return;
            }
        }

        setEntryBusy(true);
        try {
            if (entryMode === 'purchased') {
                const result = await recordMaterialPurchase({
                    source_type: 'purchased',
                    product_type: entry.product_type,
                    product_name: productName,
                    unit: entry.unit || 'kg',
                    quantity,
                    vendor_id: entry.vendor_id ? Number(entry.vendor_id) : undefined,
                    supplier_name: entry.supplier_name.trim() || undefined,
                    amount: Number(entry.amount),
                    currency: entry.currency || 'RWF',
                    spent_on: entry.spent_on || undefined,
                    received_on: entry.received_on || entry.spent_on || undefined,
                    expiry_date: entry.expiry_date || undefined,
                    quality_notes: entry.quality_notes.trim() || undefined,
                    notes: entry.notes.trim() || undefined,
                }, entryKey);
                const lot = result?.lot;
                setActionMessage(
                    `Purchase recorded${lot?.lot_code ? ` as lot ${lot.lot_code}` : ''}. `
                    + `One expense${result?.expense_id ? ` (#${result.expense_id})` : ''} was written and is pending review; `
                    + 'receipts and applications never deduct cash again.',
                );
            } else {
                const batchId = Number(String(entry.compost_batch_id).replace('batch:', ''));
                const result = await createMaterialLot({
                    source_type: 'farm_produced',
                    product_type: entry.product_type || 'compost',
                    product_name: productName,
                    unit: entry.unit || 'kg',
                    quantity,
                    compost_batch_id: batchId,
                    received_on: entry.received_on || undefined,
                    expiry_date: entry.expiry_date || undefined,
                    quality_notes: entry.quality_notes.trim() || undefined,
                    notes: entry.notes.trim() || undefined,
                });
                setActionMessage(
                    `Farm-produced lot ${result?.lot?.lot_code || ''} recorded. It carries no cash of its own; the compost batch already holds the cost.`.trim(),
                );
            }
            setEntryOpen(false);
            setEntry({
                product_type: 'compost', product_name: '', unit: 'kg', quantity: '',
                vendor_id: '', supplier_name: '', amount: '', currency: 'RWF', spent_on: '',
                received_on: '', expiry_date: '', quality_notes: '', notes: '', compost_batch_id: '',
            });
            await load();
        } catch (err: any) {
            setEntryError(err?.message || 'The lot could not be recorded.');
        } finally {
            setEntryBusy(false);
        }
    };

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const [lotRows, summaryRows] = await Promise.all([
                fetchMaterialLots({ include_depleted: 'true' }),
                fetchMaterialSummary(),
            ]);
            setLots(lotRows);
            setSummary(summaryRows);
            const [vendorRows, reviewerRows] = await Promise.all([
                fetchMaterialVendors().catch(() => []),
                fetchExpenseReviewers().catch(() => []),
            ]);
            setVendors(vendorRows);
            setReviewers(reviewerRows);
        } catch (err: any) {
            setError(
                err?.message
                || 'Material stock could not be loaded. Apply migrations 079_material_lot_chain.sql, 080_external_vendors.sql and 081_farm_expense_payments.sql in order.',
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const visibleLots = useMemo(() => lots.filter((lot) => {
        if (productFilter !== 'all' && lot.product_type !== productFilter) return false;
        if (sourceFilter !== 'all' && lot.source_type !== sourceFilter) return false;
        if (paymentFilter !== 'all' && lot.payment_status !== paymentFilter) return false;
        return true;
    }), [lots, productFilter, sourceFilter, paymentFilter]);

    const stockWarnings = useMemo(() => {
        const warnings: string[] = [];
        lots.forEach((lot) => {
            const remaining = daysUntil(lot.expiry_date);
            if (remaining !== null && remaining < 0) {
                warnings.push(`${lot.lot_code} expired ${Math.abs(remaining)} day(s) ago.`);
            } else if (remaining !== null && remaining <= 30) {
                warnings.push(`${lot.lot_code} expires in ${remaining} day(s).`);
            }
            if (lot.status === 'disputed') warnings.push(`${lot.lot_code} is disputed; it cannot be issued.`);
        });
        return warnings;
    }, [lots]);

    const openDetail = async (lot: MaterialLot) => {
        if (expandedId === lot.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(lot.id);
        setReceiptDraft('');
        setReceiptNote('');
        setActionMessage('');
        setActionError('');
        if (!movements[lot.id]) {
            try {
                const detail = await fetchMaterialLot(lot.id);
                setMovements((prev) => ({ ...prev, [lot.id]: detail.movements }));
            } catch (err: any) {
                setActionError(err?.message || 'Movement history is unavailable.');
            }
        }
        if (lot.expense_id && !payments[lot.id]) {
            try {
                const rows = await fetchExpensePayments(lot.expense_id);
                setPayments((prev) => ({ ...prev, [lot.id]: rows }));
            } catch (err: any) {
                setActionError(err?.message || 'Payment history is unavailable.');
            }
        }
    };

    const setReviewer = async (lot: MaterialLot, reviewer: string) => {
        if (!lot.expense_id) return;
        setReviewerBusy(lot.id);
        setActionError('');
        try {
            await assignExpenseReviewer(lot.expense_id, reviewer ? Number(reviewer) : null);
            setActionMessage(reviewer ? 'Named expense reviewer updated.' : 'Named reviewer cleared; the workspace owner can review this expense.');
            await load();
        } catch (err: any) {
            setActionError(err?.message || 'Reviewer could not be updated.');
        } finally {
            setReviewerBusy(null);
        }
    };

    const submitPayment = async (lot: MaterialLot) => {
        const amount = Number(paymentDraft.amount);
        if (!lot.expense_id || !Number.isFinite(amount) || amount <= 0) {
            setActionError('Enter a valid payment amount for a linked purchase expense.');
            return;
        }
        setPaymentBusy(true);
        setActionError('');
        try {
            const key = paymentKeys[lot.id] || newIdempotencyKey();
            setPaymentKeys((current) => ({ ...current, [lot.id]: key }));
            await recordExpensePayment(lot.expense_id, {
                amount,
                method: paymentDraft.method,
                reference: paymentDraft.reference.trim() || undefined,
                proof_urls: paymentDraft.proof_url.trim() ? [paymentDraft.proof_url.trim()] : [],
            }, key);
            setPaymentKeys((current) => { const next = { ...current }; delete next[lot.id]; return next; });
            setPaymentDraft({ amount: '', method: 'mobile_money', reference: '', proof_url: '' });
            setActionMessage('Payment recorded separately from expense approval.');
            await load();
            const rows = await fetchExpensePayments(lot.expense_id);
            setPayments((current) => ({ ...current, [lot.id]: rows }));
        } catch (err: any) {
            setActionError(err?.message || 'Payment could not be recorded. Retry will reuse the same request key.');
        } finally {
            setPaymentBusy(false);
        }
    };

    const submitReceipt = async (lot: MaterialLot) => {
        const quantity = Number(receiptDraft);
        if (!Number.isFinite(quantity) || quantity < 0) {
            setActionError('Enter the inspected quantity (0 or more).');
            return;
        }
        setBusyId(lot.id);
        setActionError('');
        try {
            await recordMaterialReceipt(lot.id, {
                received_quantity: quantity,
                delivery_id: lot.delivery_id || undefined,
                inspection_note: receiptNote || undefined,
            });
            setActionMessage(`Receipt recorded for ${lot.lot_code}. Stock changed; no cash moved.`);
            setReceiptDraft('');
            setReceiptNote('');
            setMovements((prev) => {
                const next = { ...prev };
                delete next[lot.id];
                return next;
            });
            await load();
            const refreshed = await fetchMaterialLot(lot.id);
            setMovements((prev) => ({ ...prev, [lot.id]: refreshed.movements }));
        } catch (err: any) {
            setActionError(err?.message || 'The receipt could not be recorded.');
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center text-sm font-semibold text-slate-500">
                Loading material stock…
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-3 p-6">
                <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-red-600" />
                    <div>
                        <p className="text-sm font-black text-red-800">Inputs and stock are unavailable</p>
                        <p className="mt-1 text-xs font-semibold text-red-700">{error}</p>
                    </div>
                </div>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-black text-white"
                >
                    <ArrowPathIcon className="h-4 w-4" /> Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                        <ScaleIcon className="h-5 w-5 text-emerald-600" /> Inputs &amp; stock
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600">
                        Farm-produced compost, purchased compost/manure and mineral fertiliser in one ledger.
                        A purchase carries one expense; receipts, distribution and application never deduct cash again.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold">
                        <option value="all">All products</option>
                        {Object.entries(PRODUCT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold">
                        <option value="all">All sources</option>
                        <option value="farm_produced">Farm-produced</option>
                        <option value="purchased">Purchased</option>
                    </select>
                    <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold">
                        <option value="all">All payment states</option>
                        <option value="paid">Paid</option>
                        <option value="partial">Partially paid</option>
                        <option value="unpaid">Unpaid / payable</option>
                        <option value="reversed">Reversed</option>
                    </select>
                    <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-slate-700">
                        <ArrowPathIcon className="h-4 w-4" /> Refresh
                    </button>
                    {canWrite && (
                        <>
                            <button onClick={() => openEntry('purchased')}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-black text-white hover:bg-emerald-700">
                                <PlusCircleIcon className="h-4 w-4" /> Record a purchase
                            </button>
                            <button onClick={() => openEntry('farm_produced')}
                                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-800">
                                <PlusCircleIcon className="h-4 w-4" /> Farm-produced lot
                            </button>
                        </>
                    )}
                </div>
            </div>

            {entryOpen && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-black text-slate-900">
                                {entryMode === 'purchased' ? 'Record a purchase' : 'Record a farm-produced lot'}
                            </p>
                            <p className="mt-1 max-w-2xl text-xs font-semibold text-slate-600">
                                {entryMode === 'purchased'
                                    ? 'The price you enter becomes this purchase\'s one expense; the quantity becomes its one lot. Neither is written again when stock moves.'
                                    : 'Compost the farm made carries no purchase expense. It is tied to its compost batch, which already holds the cost.'}
                            </p>
                        </div>
                        <button onClick={() => setEntryOpen(false)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-600">
                            Cancel
                        </button>
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {entryMode === 'farm_produced' && (
                            <div className="sm:col-span-2">
                                <FieldLabel label="Compost batch" hint={batchesLoading ? 'Loading batches…' : 'Only a batch that exists can become stock.'}>
                                    <select value={entry.compost_batch_id} onChange={(e) => setEntryValue('compost_batch_id', e.target.value)} className={inputClass}>
                                        <option value="">Choose the batch…</option>
                                        {batches.map((batch) => (
                                            <option key={batch.id} value={batch.id}>
                                                {batch.name}{batch.subtitle ? ` · ${batch.subtitle}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </FieldLabel>
                            </div>
                        )}
                        <FieldLabel label="Product type">
                            <select value={entry.product_type} onChange={(e) => setEntryValue('product_type', e.target.value)} className={inputClass}>
                                {PURCHASE_PRODUCTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </FieldLabel>
                        <FieldLabel label="Product name">
                            <input value={entry.product_name} onChange={(e) => setEntryValue('product_name', e.target.value)}
                                placeholder={entryMode === 'purchased' ? 'e.g. Compost 40 kg bags' : 'e.g. Farm compost'} className={inputClass} />
                        </FieldLabel>
                        <FieldLabel label="Quantity">
                            <input type="number" min="0" step="any" value={entry.quantity}
                                onChange={(e) => setEntryValue('quantity', e.target.value)} placeholder="0" className={inputClass} />
                        </FieldLabel>
                        <FieldLabel label="Unit">
                            <input value={entry.unit} onChange={(e) => setEntryValue('unit', e.target.value)} placeholder="kg" className={inputClass} />
                        </FieldLabel>
                        {entryMode === 'purchased' && (
                            <>
                                <FieldLabel label="External supplier" hint="Distributors are workspace vendors, not farmers or login accounts.">
                                    <div className="flex gap-2">
                                        <select value={entry.vendor_id} onChange={(e) => {
                                            const selected = vendors.find((vendor) => String(vendor.id) === e.target.value);
                                            setEntry((current) => ({ ...current, vendor_id: e.target.value, supplier_name: selected?.display_name || '' }));
                                        }} className={inputClass}>
                                            <option value="">Choose supplier…</option>
                                            {vendors.filter((vendor) => !vendor.is_archived).map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.display_name}</option>)}
                                        </select>
                                        <button type="button" onClick={() => setVendorCreateOpen((open) => !open)} className="shrink-0 rounded-lg border border-emerald-200 px-3 text-xs font-black text-emerald-800">New</button>
                                    </div>
                                </FieldLabel>
                                <FieldLabel label="Price paid / agreed" hint="Becomes the one expense for this purchase.">
                                    <input type="number" min="0" step="any" value={entry.amount}
                                        onChange={(e) => setEntryValue('amount', e.target.value)} placeholder="0" className={inputClass} />
                                </FieldLabel>
                                <FieldLabel label="Currency">
                                    <input value={entry.currency} onChange={(e) => setEntryValue('currency', e.target.value)} className={inputClass} />
                                </FieldLabel>
                                <FieldLabel label="Purchase date">
                                    <input type="date" value={entry.spent_on} onChange={(e) => setEntryValue('spent_on', e.target.value)} className={inputClass} />
                                </FieldLabel>
                            </>
                        )}
                        <FieldLabel label="Received on">
                            <input type="date" value={entry.received_on} onChange={(e) => setEntryValue('received_on', e.target.value)} className={inputClass} />
                        </FieldLabel>
                        <FieldLabel label="Expiry date">
                            <input type="date" value={entry.expiry_date} onChange={(e) => setEntryValue('expiry_date', e.target.value)} className={inputClass} />
                        </FieldLabel>
                        <div className="sm:col-span-2">
                            <FieldLabel label="Quality / inspection notes">
                                <input value={entry.quality_notes} onChange={(e) => setEntryValue('quality_notes', e.target.value)}
                                    placeholder="Moisture, contamination, bag condition…" className={inputClass} />
                            </FieldLabel>
                        </div>
                        <div className="sm:col-span-2">
                            <FieldLabel label="Notes">
                                <input value={entry.notes} onChange={(e) => setEntryValue('notes', e.target.value)}
                                    placeholder="Anything the reviewer should know" className={inputClass} />
                            </FieldLabel>
                        </div>
                    </div>

                    {vendorCreateOpen && entryMode === 'purchased' && (
                        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-emerald-200 bg-white p-3">
                            <div className="min-w-[220px] flex-1"><FieldLabel label="Supplier name"><input value={vendorDraft} onChange={(e) => setVendorDraft(e.target.value)} placeholder="Distributor or supplier" className={inputClass} /></FieldLabel></div>
                            <button type="button" onClick={submitVendor} disabled={vendorBusy || !vendorDraft.trim()} className="rounded-lg bg-emerald-700 px-4 py-2 text-xs font-black text-white disabled:opacity-50">{vendorBusy ? 'Adding…' : 'Add supplier'}</button>
                        </div>
                    )}

                    {entryError && (
                        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700">{entryError}</p>
                    )}

                    <div className="mt-3 flex justify-end gap-2">
                        <button onClick={() => setEntryOpen(false)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-600">
                            Cancel
                        </button>
                        <button onClick={submitEntry} disabled={entryBusy}
                            className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-black text-white disabled:opacity-60">
                            {entryBusy ? 'Saving…' : (entryMode === 'purchased' ? 'Save purchase' : 'Save lot')}
                        </button>
                    </div>
                </div>
            )}

            {actionMessage && (
                <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800">{actionMessage}</p>
            )}

            {stockWarnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-black uppercase tracking-wide text-amber-800">Quality / expiry warnings</p>
                    <ul className="mt-1 space-y-1">
                        {stockWarnings.map((warning) => (
                            <li key={warning} className="text-xs font-semibold text-amber-800">• {warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {['Lot / product', 'Source', 'Received', 'Free', 'Reserved', 'Issued', 'Applied', 'Payment', 'Status'].map((heading) => (
                                <th key={heading} className="px-3 py-2 text-left text-[10px] font-black uppercase tracking-wide text-gray-500">
                                    {heading}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {visibleLots.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-3 py-8 text-center text-sm font-semibold text-slate-500">
                                    No material lots match these filters. Receive a purchase or record a compost batch to create stock.
                                </td>
                            </tr>
                        )}
                        {visibleLots.map((lot) => {
                            const isOpen = expandedId === lot.id;
                            const evidence = [...asArray(lot.document_urls), ...asArray(lot.photo_urls)];
                            const remaining = daysUntil(lot.expiry_date);
                            return (
                                <React.Fragment key={lot.id}>
                                    <tr className="cursor-pointer hover:bg-gray-50" onClick={() => openDetail(lot)}>
                                        <td className="px-3 py-3">
                                            <p className="font-black text-gray-900">{lot.lot_code}</p>
                                            <p className="text-xs font-semibold text-gray-500">
                                                {lot.product_name} · {PRODUCT_LABELS[lot.product_type] || lot.product_type}
                                            </p>
                                            {lot.compost_batch_number && (
                                                <p className="text-[11px] text-gray-400">Batch {lot.compost_batch_number}</p>
                                            )}
                                        </td>
                                        <td className="px-3 py-3 text-xs font-semibold text-gray-600">
                                            <p>{lot.source_type === 'farm_produced' ? 'Farm-produced' : 'Purchased'}</p>
                                            {lot.supplier_name && <p className="text-gray-400">{lot.supplier_name}</p>}
                                            {lot.delivery_status && <p className="text-gray-400">delivery: {lot.delivery_status}</p>}
                                        </td>
                                        <td className="px-3 py-3 text-xs font-bold text-gray-700">{num(lot.received_quantity)} {lot.unit}</td>
                                        <td className="px-3 py-3 text-xs font-black text-emerald-700">{num(lot.free_quantity)} {lot.unit}</td>
                                        <td className="px-3 py-3 text-xs font-semibold text-amber-700">{num(lot.reserved_quantity)} {lot.unit}</td>
                                        <td className="px-3 py-3 text-xs font-semibold text-blue-700">{num(lot.issued_quantity)} {lot.unit}</td>
                                        <td className="px-3 py-3 text-xs font-semibold text-slate-700">{num((lot as any).applied_quantity)} {lot.unit}</td>
                                        <td className="px-3 py-3">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${PAYMENT_STYLES[lot.payment_status] || PAYMENT_STYLES.unpaid}`}>
                                                {lot.payment_status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-xs font-semibold text-gray-600">
                                            {lot.status}
                                            {remaining !== null && remaining < 0 && (
                                                <p className="text-[11px] font-black text-red-600">expired</p>
                                            )}
                                            {remaining !== null && remaining >= 0 && remaining <= 30 && (
                                                <p className="text-[11px] font-black text-amber-600">{remaining}d to expiry</p>
                                            )}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-slate-50">
                                            <td colSpan={9} className="px-4 py-4">
                                                <div className="grid gap-4 lg:grid-cols-2">
                                                    <div>
                                                        <p className="text-xs font-black uppercase tracking-wide text-slate-500">Stock location &amp; evidence</p>
                                                        <div className="mt-2 space-y-1 text-xs font-semibold text-slate-600">
                                                            <p>Store: {num(lot.available_quantity)} {lot.unit} · with farmers: {num(lot.issued_quantity)} {lot.unit}</p>
                                                            <p>Linked open jobs: {num((lot as any).linked_job_count)}</p>
                                                            <p>Field restriction: {(lot as any).field_restriction_name || (lot.field_restriction_id ? `Field ${lot.field_restriction_id}` : 'none')}</p>
                                                            <p>Received on: {lot.received_on || '—'}</p>
                                                            {lot.quality_notes && <p>Quality notes: {lot.quality_notes}</p>}
                                                            {evidence.length > 0 ? (
                                                                <div className="flex flex-wrap gap-2 pt-1">
                                                                    {evidence.map((url) => (
                                                                        <a key={url} href={url} target="_blank" rel="noreferrer"
                                                                            className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-[11px] font-bold text-slate-700 ring-1 ring-slate-200">
                                                                            <PaperClipIcon className="h-3.5 w-3.5" /> evidence
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            ) : <p>No evidence attached.</p>}
                                                        </div>

                                                        {canWrite && lot.source_type === 'purchased' && (
                                                            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                                                                <p className="text-xs font-black uppercase tracking-wide text-slate-500">Record receipt &amp; inspection</p>
                                                                <p className="mt-1 text-[11px] font-semibold text-slate-500">
                                                                    Physical goods only. The expense was recorded at purchase and is never repeated here.
                                                                </p>
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    <input type="number" min="0" step="any" value={receiptDraft}
                                                                        onChange={(e) => setReceiptDraft(e.target.value)}
                                                                        placeholder={`Received ${lot.unit}`}
                                                                        className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                                                                    <input value={receiptNote} onChange={(e) => setReceiptNote(e.target.value)}
                                                                        placeholder="Inspection note"
                                                                        className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                                                                    <button disabled={busyId === lot.id}
                                                                        onClick={() => submitReceipt(lot)}
                                                                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-black text-white disabled:opacity-50">
                                                                        {busyId === lot.id ? 'Saving…' : 'Record receipt'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {lot.expense_id && (
                                                            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <p className="text-xs font-black uppercase tracking-wide text-slate-500">Expense and supplier payments</p>
                                                                    <span className="text-[11px] font-bold text-slate-600">Expense {lot.expense_review_status || 'unreviewed'} · {num(lot.expense_paid_amount).toLocaleString()} / {num(lot.expense_amount).toLocaleString()} {lot.expense_currency || 'RWF'} paid</span>
                                                                </div>
                                                                {['owner', 'project_manager', 'tenant_admin'].includes(String(user?.role || '')) && lot.expense_review_status === 'pending' && (
                                                                    <label className="mt-3 block max-w-md text-[11px] font-bold text-slate-600">Named reviewer
                                                                        <select value={lot.review_assigned_to_user_id || ''} disabled={reviewerBusy === lot.id} onChange={(e) => setReviewer(lot, e.target.value)} className={`${inputClass} mt-1`}>
                                                                            <option value="">Owner review (default)</option>
                                                                            {reviewers.map((reviewer) => <option key={reviewer.id} value={reviewer.id}>{reviewer.full_name} · {reviewer.role.replace('_', ' ')}</option>)}
                                                                        </select>
                                                                    </label>
                                                                )}
                                                                {lot.expense_reviewer_name && <p className="mt-1 text-[11px] font-semibold text-slate-500">Assigned to {lot.expense_reviewer_name}</p>}
                                                                <div className="mt-2 space-y-1 text-xs text-slate-600">
                                                                    {(payments[lot.id] || []).map((payment) => <p key={payment.id}>{num(payment.amount).toLocaleString()} {payment.currency} via {payment.method.replace('_', ' ')} · {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : ''}{payment.reference ? ` · ${payment.reference}` : ''}{Array.isArray(payment.proof_urls) && payment.proof_urls.map((url: string) => <a key={url} href={url} target="_blank" rel="noreferrer" className="ml-2 text-emerald-700 underline">proof</a>)}</p>)}
                                                                    {!(payments[lot.id] || []).length && <p>No payment events recorded.</p>}
                                                                </div>
                                                                {canSettle && lot.expense_review_status === 'reviewed' && lot.payment_status !== 'paid' && lot.payment_status !== 'reversed' && (
                                                                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                                                        <input type="number" min="0.01" step="0.01" value={paymentDraft.amount} onChange={(e) => setPaymentDraft((d) => ({ ...d, amount: e.target.value }))} placeholder="Payment amount" className={inputClass} />
                                                                        <select value={paymentDraft.method} onChange={(e) => setPaymentDraft((d) => ({ ...d, method: e.target.value }))} className={inputClass}><option value="mobile_money">Mobile money</option><option value="bank">Bank</option><option value="cash">Cash</option><option value="other">Other</option></select>
                                                                        <input value={paymentDraft.reference} onChange={(e) => setPaymentDraft((d) => ({ ...d, reference: e.target.value }))} placeholder="Payment reference" className={inputClass} />
                                                                        <input value={paymentDraft.proof_url} onChange={(e) => setPaymentDraft((d) => ({ ...d, proof_url: e.target.value }))} placeholder="Proof URL (optional)" className={inputClass} />
                                                                        <button type="button" disabled={paymentBusy} onClick={() => submitPayment(lot)} className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-black text-white disabled:opacity-50 sm:col-span-2">{paymentBusy ? 'Recording…' : 'Record supplier payment'}</button>
                                                                    </div>
                                                                )}
                                                                {lot.expense_review_status !== 'reviewed' && <p className="mt-2 text-[11px] font-semibold text-amber-700">Payment can be recorded after an authorised reviewer approves this expense.</p>}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <p className="text-xs font-black uppercase tracking-wide text-slate-500">Movement history</p>
                                                        <div className="mt-2 max-h-64 overflow-y-auto rounded-lg bg-white ring-1 ring-slate-200">
                                                            {(movements[lot.id] || []).length === 0 ? (
                                                                <p className="p-3 text-xs font-semibold text-slate-500">No movements recorded for this lot yet.</p>
                                                            ) : (
                                                                <table className="min-w-full text-xs">
                                                                    <tbody className="divide-y divide-slate-100">
                                                                        {(movements[lot.id] || []).map((movement) => (
                                                                            <tr key={movement.id}>
                                                                                <td className="px-3 py-2 font-black text-slate-700">{movement.movement_type}</td>
                                                                                <td className="px-3 py-2 font-semibold text-slate-600">{num(movement.quantity)} {movement.unit}</td>
                                                                                <td className="px-3 py-2 text-slate-500">{movement.movement_date || ''}</td>
                                                                                <td className="px-3 py-2 text-slate-500">
                                                                                    {movement.reversed_at ? 'reversed' : (movement.reason || '')}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {actionMessage && (
                                                    <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800">{actionMessage}</p>
                                                )}
                                                {actionError && (
                                                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700">{actionError}</p>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {summary.map((row) => (
                    <div key={`${row.product_type}-${row.source_type}`} className="rounded-xl border border-gray-200 bg-white p-3">
                        <p className="text-[10px] font-black uppercase tracking-wide text-gray-500">
                            {PRODUCT_LABELS[row.product_type] || row.product_type} · {row.source_type === 'farm_produced' ? 'farm' : 'purchased'}
                        </p>
                        <p className="mt-1 text-sm font-black text-gray-900">{num(row.free_quantity)} free</p>
                        <p className="text-[11px] font-semibold text-gray-500">
                            {num(row.available_quantity)} in store · {num(row.reserved_quantity)} reserved · {num(row.issued_quantity)} with farmers · {row.lot_count} lot(s)
                        </p>
                    </div>
                ))}
            </div>

            <p className="text-[11px] font-semibold text-slate-500">
                Distributed to a farmer is not applied to a field. Stock becomes an application only when an agronomist
                approves the farmer's report; that is also the single moment the kilos are deducted.
            </p>
        </div>
    );
};

export default MaterialStockWorkspace;
